import * as moment from 'moment';
import { useMemo, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import { GridWrapper } from './styles/styles'
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from "ag-grid-community";
import { LoadingIndicator } from "@nkr/components/loading-indicator";
import { IStrongBoxCenterPhysicalInventoryListing, IStrongBoxOpoPhysicalInventoryListing, IStrongBoxPhysicalInventoryListingBase } from '@nkr/models/strong-box';
import { useAppHookContext } from '@nkr/utils/rest-api';
import SortableHeaderComponent from "../recipients/recipient-shield/components/sortable-header";
import EditPhysicalInventory from "./edit-physical-inventory";
import { EntityType } from './place-order';

type TCenterAndOpo = IStrongBoxCenterPhysicalInventoryListing | IStrongBoxOpoPhysicalInventoryListing;

type TProps<T extends TCenterAndOpo> = {
    list: T[]
    loading: boolean
    error: string,
    csvFileName: string
    emptyMessage?: string
    filterSlot: React.ReactNode,
    isCenter: boolean,
    onUpdate: (id: number, count: number) => void
}

const PhysicalInventoryGrid = <T extends TCenterAndOpo>({ list, loading, error, csvFileName, emptyMessage, filterSlot, isCenter, onUpdate }: TProps<T>) => {
    const { app: { user } } = useAppHookContext();

    const apiRef = useRef<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        apiRef.current = params.api;
        params.api.sizeColumnsToFit();
    };

    const expCsv = () => {
        if (apiRef.current) {
            apiRef.current.exportDataAsCsv({
                columnGroups: false,
                fileName: csvFileName,
                columnKeys: ['centerAlias', 'createdDate', 'count', 'initials', 'quantityOnHand'],
            });
        }
    };

    const colDefs: ColDef[] = [
        {
            colId: 'rNum',
            field: 'rNum',
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.node.rowIndex + 1}</>,
            width: 20,
            hide: !isCenter
        },
        {
            field: 'opoName',
            //rowGroup:  true,
            // rowGroup: !user.opoId ? true : false,
            hide: isCenter,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>OPO</SortableHeaderComponent>,
            // showRowGroup: true,
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            hide: !isCenter,
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
        },
        {
            colId: 'createdDate',
            field: 'createdDate',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Reported Date</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <>{moment(params.data.createdDate).format('MM/DD/YYYY')}</>,
            valueGetter: (params: ValueGetterParams) => moment(params.data.createdDate).format('MM/DD/YYYY'),
        },
        {
            colId: 'count',
            field: 'count',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Physical Inventory<br />Reported</SortableHeaderComponent>,
        },
        {
            colId: 'initials',
            field: 'initials',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Coordinator<br />Initials</SortableHeaderComponent>,
        },
        {
            colId: 'quantityOnHand',
            field: 'quantityOnHand',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Quantity On Hand<br />At Report Date</SortableHeaderComponent>,
        },
        {
            colId: 'action',
            field: '',
            sortable: false,
            width: 45,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: TCenterAndOpo = params.data;

                let entityType = isCenter ? EntityType.Center : EntityType.OPO;
                let entityName = isCenter ? (d as IStrongBoxCenterPhysicalInventoryListing).centerAlias : (d as IStrongBoxOpoPhysicalInventoryListing).opoName;
                let count = d.count;
                let createdDate = d.createdDate;

                return (
                    <EditPhysicalInventory
                        loading={loading}
                        id={d.id}
                        entityType={entityType}
                        entityName={entityName}
                        count={count}
                        createdDate={createdDate}
                        error={error}
                        onUpdate={onUpdate}
                    />
                );
            },
        },
    ];

    const columnDefs = useMemo(() => colDefs, []);

    return (<>
        <Row>
            <Col sm={12}>{filterSlot}</Col>
        </Row>

        {loading && <LoadingIndicator type='spin' color='black' loading={true} message={"Loading Inventory Reports ..."} />}

        {!loading &&
            <Row>
                <Col sm={12}>
                    <GridWrapper className='ag-theme-bootstrap'>
                        <AgGridReact
                            defaultColDef={{ headerClass: 'list-header', cellClass: 'list-cell', sortable: true }}
                            columnDefs={columnDefs}
                            rowData={list}
                            headerHeight={45}
                            rowHeight={32}
                            noRowsOverlayComponentFramework={() => <div>{emptyMessage ?? 'No physical inventory records were found...'}</div>}
                            suppressRowClickSelection={true}
                            suppressCellSelection={true}
                            enableCellTextSelection={true}
                            groupDisplayType={'groupRows'}
                            groupDefaultExpanded={1}
                            groupSelectsChildren={true}
                            onGridReady={onGridReady}
                            onFirstDataRendered={(e) => e.api.sizeColumnsToFit()}
                            onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                            onRowDataChanged={(e) => e.api.sizeColumnsToFit()}
                            groupRowRendererParams={{
                                suppressCount: true,
                            }}
                        />
                    </GridWrapper>
                </Col>
            </Row>
        }
    </>);
}

export default PhysicalInventoryGrid;
